import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import type { QuestionsOverTimePoint } from '../../types/studyAnalytics';

interface Props {
  data: QuestionsOverTimePoint[];
}

export default function QuestionsOverTimeChart({ data }: Props) {
  const { t } = useTranslation();
  if (data.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-400 dark:text-slate-500">
        {t('dashboard.study.questionsOverTime.empty')}
      </p>
    );
  }

  const locale = t('dashboard.study.questionsOverTime.dateLocale');
  const formatted = data.map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
    }),
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={formatted} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--chart-grid, #e5e7eb)" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: 'var(--chart-tick, #9ca3af)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 11, fill: 'var(--chart-tick, #9ca3af)' }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--tooltip-bg, #fff)',
            border: '1px solid var(--tooltip-border, #e5e7eb)',
            borderRadius: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--tooltip-text, #111827)',
          }}
          formatter={(value) => [t('dashboard.study.questionsOverTime.tooltipFormat', { count: value as number }), t('dashboard.study.questionsOverTime.tooltipLabel')]}
        />
        <Bar
          dataKey="count"
          fill="var(--color-primary, #3b82f6)"
          radius={[4, 4, 0, 0]}
          maxBarSize={32}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
